import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Checkbox } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { formatDateTimeBR } from "@/lib/date-utils";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export type ReturnApplicationConflictChoice = {
  keepFollowUp: boolean;
  keepVaccines: boolean;
};

export type ApplicationAppointmentSummary = {
  id: string;
  starts_at: string;
};

export function ReturnApplicationConflictDialog({
  open,
  onOpenChange,
  followUpDate,
  vaccineAppointments,
  value,
  onValueChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  followUpDate: Date;
  vaccineAppointments: ApplicationAppointmentSummary[];
  value: ReturnApplicationConflictChoice | null;
  onValueChange: (value: ReturnApplicationConflictChoice) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("medicalRecords");
  const selection = value ?? { keepFollowUp: true, keepVaccines: true };
  const summaryLabel = selection.keepFollowUp
    ? selection.keepVaccines
      ? t("returnConflict.summary.keepBoth")
      : t("returnConflict.summary.keepFollowUpOnly")
    : selection.keepVaccines
      ? t("returnConflict.summary.keepVaccineOnly")
      : t("returnConflict.summary.keepNone");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t("returnConflict.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("returnConflict.description")}
          </p>

          <div className="rounded-md border border-border/60 p-3 text-sm">
            <p className="text-muted-foreground">{t("returnConflict.followUp")}</p>
            <p className="font-medium">{formatDateTimeBR(followUpDate.toISOString())}</p>
          </div>

          <div className="rounded-md border border-border/60 p-3 text-sm">
            <p className="text-muted-foreground">{t("returnConflict.vaccine")}</p>
            <ul className="mt-2 space-y-1">
              {vaccineAppointments.map((appt) => (
                <li key={appt.id} className="font-medium">
                  {formatDateTimeBR(appt.starts_at)}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <Label>{t("returnConflict.keepInCalendar")}</Label>

            <div className="grid gap-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 p-3">
                <Checkbox
                  checked={selection.keepFollowUp}
                  onCheckedChange={(checked) =>
                    onValueChange({
                      ...selection,
                      keepFollowUp: Boolean(checked),
                    })
                  }
                  className="mt-1"
                />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">{t("returnConflict.followUp")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("returnConflict.followUpHelp")}
                  </p>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 p-3">
                <Checkbox
                  checked={selection.keepVaccines}
                  onCheckedChange={(checked) =>
                    onValueChange({
                      ...selection,
                      keepVaccines: Boolean(checked),
                    })
                  }
                  className="mt-1"
                />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">{t("returnConflict.vaccine")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("returnConflict.vaccineHelp")}
                  </p>
                </div>
              </label>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">{t("returnConflict.summary.title")}</p>
                <p className="mt-1">{summaryLabel}</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.cancel")}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={!value}>
            {t("returnConflict.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
